// Display helpers + Tailwind class maps for the Bookings screen. Kept apart
// from the components so the queues + the myI panel share one source of truth
// for sailing-type pills, status badges and the cutoff countdown bands.

import type { BookingStatus, SailingType } from './mockData';

/**
 * Cutoff countdown — minutes remaining → "10h 20m" with a colour band
 * (spec §4.3): green > 4h, amber 2–4h, red < 2h, red "Missed" once null.
 * `undefined` = not computed yet (real rows — the cutoff engine is parked) →
 * neutral "—" rather than a misleading "Missed".
 */
export function formatCutoff(minutes: number | null | undefined): { label: string; tone: string } {
  if (minutes === undefined) {
    return { label: '—', tone: 'text-slate-400' };
  }
  if (minutes === null || minutes <= 0) {
    return { label: 'Missed', tone: 'text-red-600 font-semibold' };
  }
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const label = h > 0 ? `${h}h ${m}m` : `${m}m`;
  if (minutes < 120) return { label, tone: 'text-red-600 font-semibold' };
  if (minutes < 240) return { label, tone: 'text-amber-600 font-medium' };
  return { label, tone: 'text-emerald-600 font-medium' };
}

/** Sailing-type pill classes — off-peak green, peak amber, shoulder blue. */
export const SAILING_TYPE_PILL: Record<SailingType, string> = {
  'Off-peak': 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  Peak: 'bg-amber-50 text-amber-700 ring-amber-200',
  Shoulder: 'bg-blue-50 text-blue-700 ring-blue-200',
};

/** Status badge classes — mirrors the Orders list status palette. */
export const STATUS_BADGE: Record<BookingStatus, string> = {
  Unbooked: 'bg-slate-100 text-slate-600 ring-slate-300',
  Alert: 'bg-red-50 text-red-700 ring-red-200',
  Proposed: 'bg-blue-50 text-blue-700 ring-blue-200',
  Booked: 'bg-sky-50 text-sky-700 ring-sky-200',
  Confirmed: 'bg-green-50 text-green-700 ring-green-200',
  Manual: 'bg-violet-50 text-violet-700 ring-violet-200',
};

export const STATUS_DOT: Record<BookingStatus, string> = {
  Unbooked: 'bg-slate-400',
  Alert: 'bg-red-500',
  Proposed: 'bg-blue-500',
  Booked: 'bg-sky-500',
  Confirmed: 'bg-green-500',
  Manual: 'bg-violet-500',
};

/** Money — bare EUR with no decimals (sailing costs are whole-euro in mock). */
export function money(n: number): string {
  return `€${n.toLocaleString('en-IE')}`;
}

/** Midnight `offset` days from today (real current date — prototype anchor). */
export function dayFromToday(offset: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offset);
  return d;
}

/** Short delivery-day label, e.g. "Thu 25 Jun". */
export function fmtDayLabel(offset: number): string {
  return dayFromToday(offset).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
}
